import { useState } from "react";
import { cn } from "@/lib/cn";
import { FloatingToolbar } from "@/workshop/components/canvas/floating-toolbar";
import { AgentDock } from "@/workshop/components/canvas/agent-dock";
import { SpaceSwitcher } from "@/workshop/components/canvas/space-switcher";
import { AgentAvatar } from "@/workshop/components/canvas/agent-avatar";
import { AgentCursorOverlay } from "@/workshop/components/cursors/agent-cursor-overlay";
import {
  AgentCursor,
  type AgentCursorProps,
} from "@/workshop/components/cursors/agent-cursor";

// --- Mock Data ---

const MOCK_AGENTS = [
  { id: "a1", letter: "C", statusColor: "bg-accent", color: "#818CF8" },
  { id: "a2", letter: "R", statusColor: "bg-warning", color: "#F472B6" },
  { id: "a3", letter: "W", statusColor: "bg-muted", color: "#34D399" },
];

const MOCK_SPACES = [
  { id: "s1", label: "Canvas Alpha", active: true },
  { id: "s2", label: "Research Board" },
  { id: "s3", label: "Drafts" },
];

const MOCK_CURSORS = [
  {
    id: "cursor-1",
    name: "Coder",
    color: "#818CF8",
    x: 200,
    y: 150,
    toolCall: "edit_file",
  },
  {
    id: "cursor-2",
    name: "Researcher",
    color: "#F472B6",
    x: 450,
    y: 300,
    toolCall: "web_search",
  },
  { id: "cursor-3", name: "Writer", color: "#34D399", x: 120, y: 400 },
];

const CURSOR_STYLE_PREVIEWS: Array<{
  id: string;
  label: string;
  description: string;
  cursor: AgentCursorProps;
}> = [
  {
    id: "s1-pointer",
    label: "S1 · Pointer only",
    description: "Colored arrow with no attached status bubble.",
    cursor: { agentName: "Denker", agentColor: "#34D399" },
  },
  {
    id: "s2-narration",
    label: "S2 · Pointing narration",
    description: "Neutral narration attached to the current screen point.",
    cursor: {
      agentName: "Denker",
      agentColor: "#34D399",
      content: "This is the control I mean.",
      statusAppearance: "presence",
    },
  },
  {
    id: "s3-thinking",
    label: "S3 · Undocked thinking",
    description: "Neutral Denker orb without a cursor arrow.",
    cursor: {
      agentName: "Denker",
      agentColor: "#34D399",
      thinking: true,
      pointing: false,
      statusAppearance: "presence",
    },
  },
  {
    id: "s4-status",
    label: "S4 · Undocked status",
    description: "Neutral Denker card without a cursor arrow.",
    cursor: {
      agentName: "Denker",
      agentColor: "#34D399",
      toolCall: "Drawing the cat outline…",
      pointing: false,
      statusAppearance: "presence",
    },
  },
  {
    id: "s6-callout",
    label: "S6 · Visual annotation callout",
    description:
      "Identity-free colored content, only when paired with drawing or highlighting.",
    cursor: {
      agentName: "Denker",
      agentColor: "#34D399",
      content: "The drawing stays here for reference.",
      pointing: false,
      statusAppearance: "annotation",
    },
  },
  {
    id: "s7-error",
    label: "S7 · Undocked error",
    description: "Neutral presence card with terminal error emphasis.",
    cursor: {
      agentName: "Denker",
      agentColor: "#34D399",
      error: "Could not finish the screen action.",
      pointing: false,
      statusAppearance: "presence",
    },
  },
  {
    id: "s8-long-reply",
    label: "S8 · Long undocked reply",
    description: "Neutral presence card showing the wrapping text boundary.",
    cursor: {
      agentName: "Denker",
      agentColor: "#34D399",
      content:
        "I captured the drawing screen and kept the full explanation together across the available width.",
      pointing: false,
      statusAppearance: "presence",
    },
  },
];

const PRESENCE_THEME_PREVIEWS: Array<{
  id: "dark" | "light" | "clear";
  label: string;
  stageClassName: string;
  labelClassName: string;
}> = [
  {
    id: "dark",
    label: "Dark",
    stageClassName: "bg-[#0A0A0B]",
    labelClassName: "text-white/55",
  },
  {
    id: "light",
    label: "Light",
    stageClassName: "bg-[#EEF1F5]",
    labelClassName: "text-black/50",
  },
  {
    id: "clear",
    label: "Clear",
    stageClassName:
      "bg-[linear-gradient(135deg,#536575_0%,#273440_48%,#171D25_100%)]",
    labelClassName: "text-white/55",
  },
];

// --- Page Component ---

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-3 mt-8 text-sm font-semibold uppercase tracking-wider text-muted">
      {children}
    </h2>
  );
}

export function CanvasPage() {
  const [activeSpace, setActiveSpace] = useState("s1");

  return (
    <div className="flex flex-col gap-4 p-6" data-testid="canvas-page">
      <h1 className="text-lg font-semibold text-primary">
        Canvas & Cursor Components
      </h1>
      <p className="text-sm text-secondary">
        Workshop showcase of canvas overlays, cursor components, and toolbar.
      </p>

      <SectionTitle>Floating Toolbar</SectionTitle>
      <div className="flex gap-4">
        <FloatingToolbar
          zoomLevel={100}
          onPan={() => console.log("[workshop] Pan")}
          onZoomIn={() => console.log("[workshop] Zoom in")}
          onZoomOut={() => console.log("[workshop] Zoom out")}
          onComment={() => console.log("[workshop] Comment")}
          onNewFrame={() => console.log("[workshop] New frame")}
        />
      </div>

      <SectionTitle>Agent Dock</SectionTitle>
      <div className="flex flex-wrap gap-4">
        <AgentDock
          agents={MOCK_AGENTS}
          onAddAgent={() => console.log("[workshop] Add agent")}
        />
        <AgentDock
          agents={[]}
          onAddAgent={() => console.log("[workshop] Add agent")}
        />
      </div>

      <SectionTitle>Space Switcher</SectionTitle>
      <div className="flex gap-4">
        <SpaceSwitcher
          spaces={MOCK_SPACES.map((s) => ({
            ...s,
            active: s.id === activeSpace,
          }))}
          onSelect={setActiveSpace}
          onAddSpace={() => console.log("[workshop] Add space")}
        />
      </div>

      <SectionTitle>Agent Avatars</SectionTitle>
      <div className="flex items-end gap-4">
        <AgentAvatar
          letter="C"
          size="sm"
          color="#818CF8"
          data-testid="avatar-sm"
        />
        <AgentAvatar
          letter="R"
          size="default"
          color="#F472B6"
          statusColor="bg-warning"
          data-testid="avatar-default"
        />
        <AgentAvatar
          letter="W"
          size="lg"
          color="#34D399"
          statusColor="bg-muted"
          data-testid="avatar-lg"
        />
        <AgentAvatar
          letter="A"
          size="default"
          hideStatus
          data-testid="avatar-no-status"
        />
      </div>

      <SectionTitle>Current Agent Cursor & Bubble Styles</SectionTitle>
      <p className="text-xs text-muted">
        Green gradient means visual annotation. Narration, progress, and agent
        status use neutral system glass.
      </p>
      <div
        className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-4 rounded-xl bg-[#0A0A0B] p-4"
        data-testid="cursor-style-gallery"
      >
        {CURSOR_STYLE_PREVIEWS.map((preview) => (
          <div
            key={preview.id}
            className="min-h-[220px] overflow-hidden rounded-lg border border-white/[0.08] bg-white/[0.03] p-4"
            data-testid={`cursor-style-${preview.id}`}
          >
            <h3 className="text-sm font-semibold text-white">
              {preview.label}
            </h3>
            <p className="mt-1 max-w-[34ch] text-xs leading-relaxed text-white/55">
              {preview.description}
            </p>
            <div className="mt-7 min-h-[72px] pl-3">
              <AgentCursor
                {...preview.cursor}
                className="w-full"
                data-testid={`cursor-style-${preview.id}-preview`}
              />
            </div>
          </div>
        ))}
      </div>

      <SectionTitle>Neutral Status Theme Parity</SectionTitle>
      <p className="text-xs text-muted">
        The S4 surface keeps the same layout while its material and neutral logo
        follow the active Denker theme.
      </p>
      <div className="grid gap-4 md:grid-cols-3">
        {PRESENCE_THEME_PREVIEWS.map((preview) => (
          <div
            key={preview.id}
            className={cn(
              "min-h-[130px] rounded-lg border border-border/30 p-4",
              preview.stageClassName,
            )}
            data-testid={`cursor-presence-theme-${preview.id}`}
          >
            <div
              className={cn(
                "text-xs font-medium uppercase tracking-wide",
                preview.labelClassName,
              )}
            >
              {preview.label}
            </div>
            <div className="mt-7">
              <AgentCursor
                agentName="Denker"
                agentColor="#34D399"
                toolCall="Drawing the cat outline…"
                pointing={false}
                statusAppearance="presence"
                presenceTheme={preview.id}
                className="w-full"
                data-testid={`cursor-presence-theme-${preview.id}-preview`}
              />
            </div>
          </div>
        ))}
      </div>

      <SectionTitle>Cursor — Fade Behavior</SectionTitle>
      <p className="text-xs text-muted">
        Fade in 300ms on first activity. Fade out 3s after going idle. Shown as
        static opacity levels for reference.
      </p>
      <div className="flex flex-col gap-6 pl-4 pt-4">
        <div className="flex items-center gap-8">
          <div className="opacity-30">
            <AgentCursor
              agentName="Writer"
              agentColor="#34D399"
              data-testid="cursor-fade-in"
            />
          </div>
          <span className="text-canvas-lg text-muted">
            Fading in — 300ms ease-in
          </span>
        </div>
        <div className="flex items-center gap-8">
          <AgentCursor
            agentName="Writer"
            agentColor="#34D399"
            data-testid="cursor-fade-active"
          />
          <span className="text-canvas-lg text-muted">
            Active — full opacity while working
          </span>
        </div>
        <div className="flex items-center gap-8">
          <div className="opacity-30">
            <AgentCursor
              agentName="Writer"
              agentColor="#34D399"
              data-testid="cursor-fade-out"
            />
          </div>
          <span className="text-canvas-lg text-muted">
            Fading out — 3s after last activity
          </span>
        </div>
      </div>

      <SectionTitle>Cursor — Overlap on Same Frame</SectionTitle>
      <p className="text-xs text-muted">
        When multiple agents target the same frame, cursors offset to avoid
        stacking.
      </p>
      <div className="relative h-[100px] pl-4 pt-4">
        <div className="absolute" style={{ transform: "translate(0px, 0px)" }}>
          <AgentCursor
            agentName="Coder"
            agentColor="#818CF8"
            toolCall="edit_file"
            statusAppearance="presence"
            className="w-[260px]"
            data-testid="cursor-overlap-1"
          />
        </div>
        <div
          className="absolute"
          style={{ transform: "translate(30px, 28px)" }}
        >
          <AgentCursor
            agentName="Researcher"
            agentColor="#F472B6"
            toolCall="read_file"
            statusAppearance="presence"
            className="w-[260px]"
            data-testid="cursor-overlap-2"
          />
        </div>
      </div>

      {/* Cursor Overlay in contained box */}
      <SectionTitle>Cursor Overlay (Contained)</SectionTitle>
      <p className="text-xs text-muted">
        Multiple cursors on canvas with 150ms smooth position interpolation.
      </p>
      <div className="relative h-[300px] overflow-hidden rounded-lg border border-border bg-canvas">
        <div
          className="pointer-events-none absolute inset-0"
          aria-hidden="true"
          style={{
            backgroundImage:
              "radial-gradient(circle, var(--color-border) 1px, transparent 1px)",
            backgroundSize: "20px 20px",
          }}
        />
        <AgentCursorOverlay cursors={MOCK_CURSORS} />
      </div>

      <div className="h-12" />
    </div>
  );
}
